import { forwardRef, useEffect, useRef, useState } from 'react';
import { DateUtils } from 'react-day-picker';
import type {
  BeforeModifier,
  AfterModifier,
  BeforeAfterModifier,
} from 'react-day-picker';
import { DatePickerInput } from '../DatePicker/DatePickerInput';
import type { DatePickerInputProps } from '../DatePicker/DatePickerInput';
import { DateRangePickerContainer } from '../DatePicker/styles';

export type DateRange = {
  /**
   * Value of the start date.
   */
  from?: string | Date;
  /**
   * Value of the end date
   */
  to?: string | Date;
};

export interface DateRangePickerProps
  extends Pick<
    DatePickerInputProps,
    'size' | 'preventFutureDate' | 'preventDatesBefore'
  > {
  /**
   * The unique ID (attribute) of the html input element for the start date.
   */
  fromId?: string;
  /**
   * The unique ID (attribute) of the html input element for the end date.
   */
  toId?: string;
  /**
   * Name of the start date input, for form purposes.
   */
  fromName?: string;
  /**
   * Name of the end date input, for form purposes.
   */
  toName?: string;
  /**
   * Range for the DateRangePicker.
   */
  range?: DateRange;
  /**
   * Callback when range is changed, either via the inputs or the picker.
   */
  onDateChange?: (range: { from?: Date; to?: Date }) => void;
  /**
   * Specifies if the input for the start date is disabled.
   */
  fromIsDisabled?: boolean;
  /**
   * Specifies if the input for the end date is disabled.
   */
  toIsDisabled?: boolean;
  /**
   * Specifies if the input for the start date is invalid. When set to true, we'll render the input in invalid state.
   */
  fromIsInvalid?: boolean;
  /**
   * Specifies if the input for the end date is invalid. When set to true, we'll render the input in invalid state.
   */
  toIsInvalid?: boolean;
  /**
   * Styles overrides.
   */
  className?: string;
}

const parseDate = (date?: string | Date) => {
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'string') {
    return new Date(date);
  }
  return undefined;
};

const getFromDisabledDays = (
  toDate: Date,
  preventDatesBefore?: Date,
): BeforeModifier | AfterModifier | BeforeAfterModifier => {
  const disabledDays: { after: Date; before?: Date } = { after: toDate };

  if (DateUtils.isDate(preventDatesBefore)) {
    disabledDays.before = preventDatesBefore;
  }

  return disabledDays;
};

const getToDisabledDays = (
  fromDate: Date,
  preventDatesBefore?: Date,
): BeforeModifier => {
  if (
    !DateUtils.isDate(preventDatesBefore) ||
    (DateUtils.isDate(fromDate) &&
      DateUtils.isDayAfter(fromDate, preventDatesBefore))
  ) {
    return { before: fromDate };
  }

  return { before: preventDatesBefore };
};

const toStartOfDay = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const toEndOfDay = (date: Date) => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + 1,
    0,
    0,
    0,
    -1,
  );
};

/**
 * DateRangePicker component.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/39b51a-date-picker Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-daterangepicker--basic-date-range-picker Code documentation}
 *
 * @example
 * <DateRangePicker />
 */
export const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      range,
      onDateChange,
      fromId,
      toId,
      fromName,
      toName,
      fromIsDisabled = false,
      toIsDisabled,
      fromIsInvalid = false,
      toIsInvalid = false,
      preventDatesBefore,
      size = 'm',
      preventFutureDate = false,
      ...props
    }: DateRangePickerProps,
    ref,
  ) => {
    const [fromDate, setFromDate] = useState<Date>();
    const [toDate, setToDate] = useState<Date>();
    const toRef = useRef<HTMLInputElement>();

    useEffect(() => {
      setFromDate(parseDate(range?.from));
      setToDate(parseDate(range?.to));
    }, [range?.from, range?.to]);

    const modifiers = { start: fromDate, end: toDate };

    return (
      <DateRangePickerContainer ref={ref}>
        <DatePickerInput
          id={fromId}
          name={fromName}
          isDisabled={fromIsDisabled}
          isInvalid={fromIsInvalid}
          size={size}
          preventFutureDate={preventFutureDate}
          {...props}
          value={fromDate}
          dayPickerProps={{
            selectedDays: [fromDate, { from: fromDate, to: toDate }],
            disabledDays: getFromDisabledDays(toDate, preventDatesBefore),
            onDayClick: (day: Date) => {
              if (day > toDate) {
                return;
              }
              toRef.current?.focus();
            },
            modifiers,
            className: 'DayPicker--from',
          }}
          onDateChange={(date: Date | undefined) => {
            const newFromDate = date ? toStartOfDay(date) : undefined;
            setFromDate(newFromDate);
            onDateChange && onDateChange({ from: newFromDate, to: toDate });
          }}
        />
        <DatePickerInput
          id={toId}
          name={toName}
          isDisabled={toIsDisabled}
          isInvalid={toIsInvalid}
          size={size}
          preventFutureDate={preventFutureDate}
          {...props}
          value={toDate}
          inputRef={toRef}
          dayPickerProps={{
            selectedDays: [fromDate, { from: fromDate, to: toDate }],
            disabledDays: getToDisabledDays(fromDate, preventDatesBefore),
            modifiers,
            className: 'DayPicker--to',
          }}
          onDateChange={(date: Date | undefined) => {
            const newToDate = date ? toEndOfDay(date) : undefined;
            setToDate(newToDate);
            onDateChange && onDateChange({ from: fromDate, to: newToDate });
          }}
        />
      </DateRangePickerContainer>
    );
  },
);

DateRangePicker.displayName = 'DateRangePicker';
